import {
  DataRouter,
  DataStrategyFunction,
  RouteObject,
  UNSAFE_createBrowserHistory,
  UNSAFE_createRouter,
  UNSAFE_hydrationRouteProperties,
  UNSAFE_defaultMapRouteProperties,
} from "react-router";
import { Environment } from "relay-runtime";
import routes from "../routes";
import { reload } from "@/utils/relay";

export function createRouter(
  relay: Environment,
  shouldHydrate: boolean,
  context: Window = window,
): DataRouter {
  // Prevent running route loaders on first navigation because the page has already been rendered on the server
  let shouldLoadMatches = !shouldHydrate;

  // Data strategy function is first called on hydration, and then on each navigation action.
  const dataStrategy: DataStrategyFunction = async ({
    matches,
    runClientMiddleware,
  }) => {
    if (!shouldLoadMatches) {
      shouldLoadMatches = true;
      // Loaders already ran on the server, but react-router still asks us to "load" every
      // lazy-unresolved match (it can't yet know which ones have loaders) and throws
      // "No result returned from dataStrategy" for any requested match that has neither a
      // result entry nor SSR loaderData. Resolve each match without invoking its loader,
      // then return results only for the routes that turn out to have none: loader-having
      // routes must be omitted so their server-provided loaderData is not overwritten.
      const resolved = await Promise.all(
        matches
          .filter((match) => match.shouldCallHandler())
          .map(async (match) => ({
            match,
            result: await match.resolve(() => undefined),
          })),
      );
      return Object.fromEntries(
        resolved
          .filter(({ match }) => !match.route.loader)
          .map(({ match, result }) => [match.route.id, result]),
      );
    }

    const matchesToLoad = matches.filter((m) =>
      m.shouldCallHandler(shouldLoadMatches),
    );
    return runClientMiddleware(async () =>
      Object.fromEntries(
        await Promise.all(
          matchesToLoad.map(async (match) => [
            match.route.id,
            await match.resolve(),
          ]),
        ),
      ),
    );
  };

  // Extend how route properties are defined:
  // we want to merge lazily-defined handles with eagerly-defined ones.
  const mapRouteProperties: typeof UNSAFE_defaultMapRouteProperties = (
    route,
  ) => {
    const updates = UNSAFE_defaultMapRouteProperties(route);

    if (isRouteWithLazyHandle(route)) {
      updates.handle = { ...route.handle, ...route.lazyHandle };
    }

    return updates;
  };

  // Basically just inlined `createBrowserRouter` here.
  const router = UNSAFE_createRouter({
    history: UNSAFE_createBrowserHistory({ window: context }),
    hydrationData: shouldHydrate ? getHydrationData(relay, context) : undefined,
    routes: routes(relay),
    mapRouteProperties,
    hydrationRouteProperties: UNSAFE_hydrationRouteProperties,
    dataStrategy,
    window: context,
  });

  return router.initialize();
}

function getHydrationData(relay: Environment, context: Window) {
  if (!("__staticRouterHydrationData" in context)) {
    return undefined;
  }

  const data =
    context.__staticRouterHydrationData as typeof window.__staticRouterHydrationData;
  if (!data) {
    return undefined;
  }

  const hydrationData = { ...data };

  if (hydrationData.loaderData) {
    for (const k in hydrationData.loaderData) {
      const key = k as keyof typeof hydrationData.loaderData;
      if (hydrationData.loaderData[key]) {
        hydrationData.loaderData[key] = reload(
          relay,
          hydrationData.loaderData[key],
        );
      }
    }
  }

  return hydrationData;
}

interface RouteWithLazyHandle {
  lazyHandle: object;
}

function isRouteWithLazyHandle(
  route: RouteObject,
): route is RouteObject & RouteWithLazyHandle {
  return "lazyHandle" in route && typeof route.lazyHandle === "object";
}
